import React, { useEffect, useState } from 'react';
import { useSecureAdminAuth } from '@/hooks/useSecureAdminAuth';
import SecureAdminLogin from './SecureAdminLogin';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Shield, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface EnhancedSecureAdminProtectionProps {
  children: React.ReactNode;
}

const EnhancedSecureAdminProtection: React.FC<EnhancedSecureAdminProtectionProps> = ({ children }) => {
  const { isAdminAuthenticated, isLoading, adminUser, createAuditLog } = useSecureAdminAuth();
  const { toast } = useToast();
  const [isLocationVerified, setIsLocationVerified] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    // Vérification de l'URL d'accès
    const currentPath = window.location.pathname;
    if (!currentPath.startsWith('/secure-admin-feel-2024')) {
      setIsLocationVerified(false);
      return;
    }
    setIsLocationVerified(true);

    // Log de tentative d'accès
    if (adminUser) {
      createAuditLog('admin_page_access', { 
        page: currentPath,
        timestamp: new Date().toISOString()
      });
    }

    // Session timeout (30 minutes)
    const sessionTimeout = setTimeout(() => {
      if (isAdminAuthenticated) {
        setSessionExpired(true);
        toast({
          title: "Session expirée",
          description: "Votre session administrateur a expiré pour des raisons de sécurité.",
          variant: "destructive",
        });
      }
    }, 30 * 60 * 1000); // 30 minutes

    return () => clearTimeout(sessionTimeout);
  }, [adminUser, isAdminAuthenticated, createAuditLog, toast]);

  // Vérification de l'URL sécurisée
  if (!isLocationVerified) {
    return (
      <div className="min-h-screen bg-feel-blanc-brume flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-semibold mb-2 text-feel-bleu-vert-fonce">
              Accès non autorisé
            </h2>
            <p className="text-feel-gris-doux">
              Cette page n'existe pas ou vous n'avez pas les droits d'accès nécessaires.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-feel-blanc-brume flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-feel-bleu-vif" />
          <p className="text-feel-gris-doux">Vérification sécurisée en cours...</p>
        </div>
      </div>
    );
  }

  if (sessionExpired) {
    return <SecureAdminLogin message="Session expirée. Veuillez vous reconnecter." />;
  }

  if (!isAdminAuthenticated) {
    return <SecureAdminLogin />;
  }

  return (
    <>
      {/* Indicateur de sécurité active */}
      <div className="fixed top-0 right-0 z-50 m-4">
        <Alert className="w-auto bg-green-50 border-green-200">
          <Shield className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800 text-sm">
            Session sécurisée - {adminUser?.email}
          </AlertDescription>
        </Alert>
      </div>
      {children}
    </>
  );
};

export default EnhancedSecureAdminProtection;